import {
  createAction,
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";
import {
  PROMO_CODE_APPLIED,
  PROMO_CODE_ERROR,
  PROMO_CODE_REMOVED,
} from "../../../constants/messages/promotions.js";
import useCookies from "../../../hooks/useCookies.js";
import { offerApi } from "../../offer/api/offerApi.js";
import { cartApi } from "../api/cartApi.js";

const { getCookie } = useCookies();

const initialState = {
  GearDamageOptIn: true,
  GuaranteedDeliveryOptIn: false,
  appliedPromoCode: getCookie(PROMO_CODE_APPLIED.CookiePrefix) ?? "",
  error: null,
  Status: "",
  isLoading: false,
};

const applyPromoCodeAction = createAction("cart/applyPromoCode");
const removePromoCodeAction = createAction("cart/removePromoCode");

/**
 * Custom Thunks to form disctinction between regular `getcartitems` requests & applying or removing a promo code for different logic.
 */
export const applyPromoCode = createAsyncThunk(
  applyPromoCodeAction.type,
  async (payload, thunkApi) => {
    const { getCartItems } = cartApi.endpoints;
    const { fulfillWithValue, rejectWithValue, dispatch } = thunkApi;
    const { setCookie } = useCookies();

    try {
      const response = await dispatch(getCartItems.initiate(payload));
      if (response.isSuccess && response.data) {
        const { Cart } = response.data.data;

        const { PromoCode } = Cart;

        if (!PromoCode || PromoCode !== payload.promoCode) {
          return rejectWithValue(PROMO_CODE_ERROR);
        }

        setCookie(PROMO_CODE_APPLIED.CookiePrefix, payload.promoCode);

        const Effects = Object.entries(Cart)
          .filter(([key, value]) => key.startsWith("PromoEffect") && value > 0)
          .reduce((acc, [key, value], index) => {
            acc[index] = { key, value };

            return acc;
          }, []);

        return fulfillWithValue(Effects);
      } else {
        return rejectWithValue(PROMO_CODE_APPLIED.Error);
      }
    } catch (error) {
      return rejectWithValue(PROMO_CODE_APPLIED.Error);
    }
  },
);

export const removePromoCode = createAsyncThunk(
  removePromoCodeAction.type,
  async (_, thunkApi) => {
    const { deleteCookie } = useCookies();

    deleteCookie(PROMO_CODE_APPLIED.CookiePrefix);

    const { dispatch, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      dispatch(cartApi.util.invalidateTags(["Cart"]));

      return fulfillWithValue(PROMO_CODE_REMOVED.Success);
    } catch (error) {
      return rejectWithValue(PROMO_CODE_REMOVED.Error);
    }
  },
);

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    toggleGearDamageOptIn: (state) => {
      state.GearDamageOptIn = !state.GearDamageOptIn;
    },
    updateDeliveryMethod: (state, { payload }) => {
      state.GuaranteedDeliveryOptIn = payload === "guaranteed";
    },
    setAppliedPromoCode: (state, { payload }) => {
      state.appliedPromoCode = payload ?? "";
    },
  },
  extraReducers: (builder) => {
    const { getOffer } = offerApi.endpoints;
    const { getCartItems, addCartItem } = cartApi.endpoints;
    builder
      .addCase(applyPromoCode.fulfilled, (state, action) => {
        state.appliedPromoCode = action.meta.arg.promoCode ?? "";
      })
      .addCase(removePromoCode.fulfilled, (state) => {
        state.appliedPromoCode = "";
      })
      .addMatcher(
        isAnyOf(
          getCartItems.matchFulfilled,
          getCartItems.matchRejected,
          addCartItem.matchRejected,
          addCartItem.matchFulfilled,
        ),
        (state, _action) => {
          state.isLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(addCartItem.matchPending, getCartItems.matchPending),
        (state, _action) => {
          state.isLoading = true;
        },
      )
      .addMatcher(getOffer.matchFulfilled, (state, { payload }) => {
        const { Modification } = payload;
        if (Modification && Modification.DeliveryFeeGuaranteed > 0) {
          state.GuaranteedDeliveryOptIn = true;
        }
      })
      .addDefaultCase((state, _action) => state);
  },
});

export const {
  toggleGearDamageOptIn,
  updateDeliveryMethod,
  setAppliedPromoCode,
} = cartSlice.actions;

export const getCart = (state) => state.cart;

export default cartSlice.reducer;
